<template>
  <section class="specializations-section">
    <div class="spec-wrapper">

      <Motion
        :initial="{ opacity: 0, y: 30 }"
        :whileInView="{ opacity: 1, y: 0 }"
        :inViewOptions="{ once: true, margin: '-80px' }"
        :transition="{ duration: 0.7, ease: 'easeOut' }"
      >
        <div class="section-header">
          <h2 class="section-title">
            <span class="gradient-text">Next-Gen</span> Specializations
          </h2>
          <p class="section-subtitle">
            Explore industry-focused specializations designed to prepare you for the technologies shaping tomorrow.
          </p>
        </div>
      </Motion>

      <div class="spec-grid">
        <Motion
          v-for="(spec, i) in specializations"
          :key="i"
          :initial="{ opacity: 0, y: 50 }"
          :whileInView="{ opacity: 1, y: 0 }"
          :inViewOptions="{ once: true, margin: '-80px' }"
          :transition="{ duration: 0.7, delay: i * 0.12, ease: 'easeOut' }"
        >
          <div class="spec-card" :style="{ '--accent': spec.accent, '--accent-rgb': spec.accentRgb }">
            <!-- Video background -->
            <video class="spec-video" :src="`https://v4devb.qspiders.com/static_videos/${spec.video}`" autoplay muted loop
              playsinline />

            <!-- Dark gradient overlay -->
            <div class="spec-overlay" />

            <!-- Card index number -->
            <span class="spec-number">{{ String(i + 1).padStart(2, '0') }}</span>

            <!-- Foreground content pinned to bottom -->
            <div class="spec-content">
              <div class="spec-icon-wrap" v-html="spec.icon" />

              <div class="spec-titles">
                <h3 class="spec-title">{{ spec.title }}</h3>
                <p v-if="spec.tag" class="spec-tag">{{ spec.tag }}</p>
              </div>

              <p class="spec-desc">{{ spec.desc }}</p>

              <button class="spec-cta" type="button">
                Explore Pathway
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                  stroke-linecap="round" stroke-linejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
            </div>
          </div>
        </Motion>
      </div>

    </div>
  </section>
</template>

<script setup lang="ts">
// ── Monochrome icons ──────────────────────────────────────────────────────────
const iconCode = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>`
const iconCpu = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="6" height="6"/><path d="M17 9h1a2 2 0 0 1 2 2v1M17 15h1a2 2 0 0 0 2-2v-1M7 9H6a2 2 0 0 0-2 2v1M7 15H6a2 2 0 0 1-2-2v-1M9 7V6a2 2 0 0 1 2-2h1M15 7V6a2 2 0 0 0-2-2h-1M9 17v1a2 2 0 0 0 2 2h1M15 17v1a2 2 0 0 1-2 2h-1"/></svg>`
const iconTerminal = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg>`
const iconLayers = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>`
const iconNetwork = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="5" r="2.5"/><circle cx="5" cy="19" r="2.5"/><circle cx="19" cy="19" r="2.5"/><line x1="12" y1="7.5" x2="5" y2="16.5"/><line x1="12" y1="7.5" x2="19" y2="16.5"/><line x1="7.5" y1="19" x2="16.5" y2="19"/></svg>`
const iconTrending = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>`

// ── Specializations data ──────────────────────────────────────────────────────
const specializations = [
  {
    title: 'Full Stack Development & Testing',
    tag: 'with AI & ML',
    desc: 'Become a versatile software engineer capable of building, testing, and deploying intelligent applications powered by AI.',
    video: 'programming.mp4',
    accent: '#3b82f6',
    accentRgb: '59,130,246',
    icon: iconCode
  },
  {
    title: 'RPA',
    tag: 'with AI & ML',
    desc: 'Create intelligent automation systems that help organizations improve efficiency and drive digital transformation.',
    video: 'robotics.mp4',
    accent: '#8b5cf6',
    accentRgb: '139,92,246',
    icon: iconCpu
  },
  {
    title: 'Software Development',
    tag: 'with AI & ML',
    desc: 'Build next-generation software products enhanced by artificial intelligence and machine learning technologies.',
    video: 'Software-development.mp4',
    accent: '#10b981',
    accentRgb: '16,185,129',
    icon: iconTerminal
  },
  {
    title: 'Computer Science & Design System',
    tag: null,
    desc: 'Develop the technical and design expertise needed to create impactful digital experiences and products.',
    video: 'Design-systems.mp4',
    accent: '#ec4899',
    accentRgb: '236,72,153',
    icon: iconLayers
  },
  {
    title: 'Artificial Intelligence & Data Science',
    tag: null,
    desc: 'Transform data into insights and build intelligent systems that power the future of technology.',
    video: 'AI.mp4',
    accent: '#ff7800',
    accentRgb: '255,120,0',
    icon: iconNetwork
  },
  {
    title: 'Fintech & Data Analytics',
    tag: null,
    desc: 'Leverage data and technology to solve challenges in modern finance, banking, and digital commerce.',
    video: 'fintech.mp4',
    accent: '#14b8a6',
    accentRgb: '20,184,166',
    icon: iconTrending
  }
]
</script>

<style scoped>
/* ── Section ─────────────────────────────────── */
.specializations-section {
  padding: 8rem 2rem;
  position: relative;
  z-index: 10;
}

.spec-wrapper {
  max-width: 1400px;
  margin: 0 auto;
}

/* ── Header ──────────────────────────────────── */
.section-header {
  text-align: center;
  margin-bottom: 4.5rem;
}

.section-eyebrow {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 187, 63, 0.7);
  margin: 0 0 1rem;
}

.section-title {
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 800;
  color: var(--text-primary);
  margin: 0 0 1.25rem;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.section-subtitle {
  font-size: 1.2rem;
  color: var(--text-secondary);
  max-width: 620px;
  margin: 0 auto;
  line-height: 1.7;
}

.gradient-text {
  background: linear-gradient(135deg, #ff7800, #ff4e00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ── Grid ────────────────────────────────────── */
.spec-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

/* ── Card ────────────────────────────────────── */
.spec-card {
  position: relative;
  height: 420px;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition:
    transform 0.45s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.4s ease,
    box-shadow 0.4s ease;
}

.spec-card:hover {
  transform: translateY(-8px) scale(1.01);
  border-color: rgba(var(--accent-rgb), 0.45);
  box-shadow:
    0 24px 64px rgba(var(--accent-rgb), 0.22),
    0 0 0 1px rgba(var(--accent-rgb), 0.12);
}

/* ── Video ───────────────────────────────────── */
.spec-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.99;
  transition: opacity 0.5s ease, transform 0.6s ease;
}

.spec-card:hover .spec-video {
  opacity: 0.88;
  transform: scale(1.06);
}

/* ── Gradient overlay ────────────────────────── */
.spec-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top,
      var(--bg-primary) 0%,
      rgba(6, 5, 24, 0.78) 38%,
      rgba(6, 5, 24, 0.35) 68%,
      rgba(0, 0, 0, 0.08) 100%);
  z-index: 1;
  transition: background 0.4s ease;
}

[data-theme="light"] .spec-overlay {
  background: linear-gradient(to top,
      rgba(255, 255, 255, 0.65) 0%,
      rgba(255, 255, 255, 0.28) 38%,
      rgba(255, 255, 255, 0.06) 68%,
      rgba(255, 255, 255, 0.02) 100%);
}

.spec-card:hover .spec-overlay {
  background: linear-gradient(to top,
      var(--bg-primary) 0%,
      rgba(6, 5, 24, 0.62) 38%,
      rgba(6, 5, 24, 0.18) 68%,
      transparent 100%);
}

[data-theme="light"] .spec-card:hover .spec-overlay {
  background: linear-gradient(to top,
      rgba(255, 255, 255, 0.55) 0%,
      rgba(255, 255, 255, 0.22) 38%,
      rgba(255, 255, 255, 0.08) 68%,
      transparent 100%);
}

/* ── Card number ─────────────────────────────── */
.spec-number {
  position: absolute;
  top: 1.25rem;
  right: 1.5rem;
  font-size: 3.75rem;
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.04em;
  color: rgba(255, 255, 255, 0.07);
  z-index: 2;
  transition: color 0.4s ease;
  user-select: none;
}

[data-theme="light"] .spec-number {
  color: rgba(0, 0, 0, 0.06);
}

/* Light mode text shadow for readability over video */
[data-theme="light"] .spec-title,
[data-theme="light"] .spec-tag,
[data-theme="light"] .spec-desc,
[data-theme="light"] .spec-cta {
  text-shadow: 0 1px 6px rgba(255, 255, 255, 0.6);
}

.spec-card:hover .spec-number {
  color: rgba(var(--accent-rgb), 0.18);
}

/* ── Content block ───────────────────────────── */
.spec-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.75rem;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

/* ── Icon ────────────────────────────────────── */
.spec-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 11px;
  background: rgba(var(--accent-rgb), 0.14);
  border: 1px solid rgba(var(--accent-rgb), 0.28);
  color: var(--accent);
  flex-shrink: 0;
  margin-bottom: 0.2rem;
  transition: background 0.35s ease, border-color 0.35s ease;
}

.spec-card:hover .spec-icon-wrap {
  background: rgba(var(--accent-rgb), 0.22);
  border-color: rgba(var(--accent-rgb), 0.45);
}

/* ── Titles ──────────────────────────────────── */
.spec-titles {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.spec-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.3;
}

.spec-tag {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent);
  opacity: 0.85;
  margin: 0;
}

/* ── Description — reveals on hover ─────────── */
.spec-desc {
  font-size: 0.845rem;
  color: var(--text-secondary);
  line-height: 1.65;
  margin: 0;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transition:
    max-height 0.42s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.32s ease;
}

.spec-card:hover .spec-desc {
  max-height: 6rem;
  opacity: 1;
}

/* ── CTA ─────────────────────────────────────── */
.spec-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--accent);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  letter-spacing: 0.02em;
  align-self: flex-start;
  opacity: 0.55;
  transform: translateY(4px);
  transition:
    opacity 0.32s ease 0.06s,
    transform 0.32s ease 0.06s,
    gap 0.22s ease;
}

.spec-card:hover .spec-cta {
  opacity: 1;
  transform: translateY(0);
}

.spec-cta:hover {
  gap: 0.65rem;
}

/* ── Responsive ──────────────────────────────── */
@media (max-width: 1024px) {
  .spec-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .specializations-section {
    padding: 4rem 1rem;
  }

  .spec-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .spec-card {
    height: 360px;
  }

  /* Always show desc + cta on mobile (no hover) */
  .spec-desc {
    max-height: 6rem;
    opacity: 1;
  }

  .spec-cta {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
